import { useEffect, useRef, useCallback, ReactNode } from "react";

interface InfiniteScrollProps {
  fetchMore: () => void;
  hasMore: boolean;
  children: ReactNode;
}

export default function InfiniteScroll({
  fetchMore,
  hasMore,
  children,
}: InfiniteScrollProps) {
  const observerRef = useRef<null | IntersectionObserver>(null);
  const loadMoreRef = useRef<null | HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore) {
        fetchMore();
      }
    },
    [fetchMore, hasMore],
  );

  useEffect(() => {
    observerRef.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    });
    const currentObserver = observerRef.current;
    const currentTarget = loadMoreRef.current;

    if (currentTarget) currentObserver.observe(currentTarget);

    return () => {
      if (currentTarget) currentObserver.unobserve(currentTarget);
    };
  }, [handleObserver]);

  return (
    <>
      {children}
      <div ref={loadMoreRef} style={{ height: "1px" }} />
    </>
  );
}
